import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { LanguageService } from '../../../../shared/services/language.service';
import { StringHelper } from '../../../../shared/utils/string-helper';
import { Occupancy, OccupancyType } from '../../../../shared/models/occupancy';
import { RoomService } from '../../../../shared/services/rest/room.service';
import { DateHelper } from '../../../../shared/utils/date-helper';
import { ScheduleItem } from '../room-schedule-item/room-schedule-item.component';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { ICreateReservationParams } from '../create-reservation-dialog/create-reservation-dialog-base.component';
import { IDisplayItem, IRoomScheduleInfo } from '../room-allocation-table/room-allocation-table.component';
import { EventoDayShortPipe } from 'src/app/shared/pipes/evento-day-short.pipe';
import { EventoDatePipe } from 'src/app/shared/pipes/evento-date.pipe';
import { DeviceService } from 'src/app/shared/services/rest/device.service';
import { ResourceType, ResourceTypeHelper } from 'src/app/shared/models/resource-type';
import { NumberHelper } from 'src/app/shared/utils/number-helper';

@Component({
  selector: 'app-room-schedule',
  templateUrl: './room-schedule.component.html',
  styleUrls: ['./room-schedule.component.scss']
})
export class RoomScheduleComponent implements OnInit {

  /**
   * Id of the room to display in calendar
   *
   * @type {number}
   * @memberof RoomScheduleComponent
   */
  @Input() roomId: number;

  /**
   * Any containig date of the week to display
   *
   * @type {Date}
   * @memberof RoomScheduleComponent
   */
  @Input() currentDate: Date;

  /**
   * Type of items to display (room or device)
   *
   * @type {ResourceType}
   * @memberof RoomScheduleComponent
   */
  @Input() type: ResourceType;

  @Input() ressourceInfo: IRoomScheduleInfo;

  /**
   * Event raised on click on "create new"
   *
   * @memberof RoomScheduleItemComponent
   */
  @Output() createReservation = new EventEmitter<ICreateReservationParams>();

  /**
   * Start hour of the table
   *
   * @memberof RoomScheduleComponent
   */
  fromHour = 6;

  /**
   * End hour of the table
   *
   * @memberof RoomScheduleComponent
   */
  toHour = 22;

  /**
   * Data-model of the component
   *
   * @type {TableModel}
   * @memberof RoomScheduleComponent
   */
  tableModel: TableModel;

  mobileTableData: IMobileTableModel[];

  items: IDisplayItem[];

  /**
   * Indicates wheter the component is currently loading data or not
   *
   * @memberof RoomScheduleComponent
   */
  isLoading = false;

  /**
   * Date of the first day to display (usually monday)
   *
   * @private
   * @type {Date}
   * @memberof RoomScheduleComponent
   */
  private firstDate: Date;

  /**
   * Date of the last day to display (usually saturday)
   *
   * @private
   * @type {Date}
   * @memberof RoomScheduleComponent
   */
  private lastDate: Date;

  constructor(
    private lang: LanguageService,
    private translate: TranslateService,
    private room: RoomService,
    private device: DeviceService,
    private eventoDayShortPipe: EventoDayShortPipe,
    private eventoDatePipe: EventoDatePipe
  ) {
  }

  /**
   * Creates the calendar title string
   *
   * @readonly
   * @type {string}
   * @memberof RoomScheduleComponent
   */
  get currentDateString(): string {
    const w = this.lang.getWeekNumber(this.firstDate);
    const m = this.lang.getMonthString(this.firstDate);
    return `${w} (${m} ${this.firstDate.getFullYear()})`;
  }

  /**
   * Map Occupancy object into ScheduleItem-Array
   *
   * @static
   * @param {Occupancy} occupancy
   * @param {Observable<string>} text
   * @returns {ScheduleItem[]}
   * @memberof RoomScheduleComponent
   */
  public static createScheduleItems(occupancy: Occupancy, defaultText: Observable<any>, minDate: Date, maxDate: Date): ScheduleItem[] {
    const array = new Array<ScheduleItem>();
    const displayColor = occupancy.Type === OccupancyType.Blocker ? '#333333' : occupancy.DisplayColor;

    const firstItem = new ScheduleItem(occupancy.ResourceId, new Date(occupancy.DateTimeFrom), new Date(occupancy.DateTimeTo),
      occupancy.Designation, occupancy.Description, defaultText, displayColor, occupancy.Type);

    if (DateHelper.AreSameDay(occupancy.DateTimeFrom, occupancy.DateTimeTo)) {
      // occupancy is on a single day
      array.push(firstItem);
    } else {
      // occupancy spans multiple days
      const startDate = DateHelper.Max(occupancy.DateTimeFrom, minDate);
      firstItem.from = new Date(startDate);
      firstItem.to = DateHelper.EndOfDay(startDate);
      array.push(firstItem);

      // increment day (YYYY-mm-dd 00:00:00.000)
      let next = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      next.setDate(next.getDate() + 1);

      const endDay = DateHelper.Min(occupancy.DateTimeTo, maxDate);
      while (next < endDay) {
        // define end of item
        const nextEnd = NumberHelper.Min(next.getTime() + 86400000 - 1, endDay.getTime());

        const intermediatItem = new ScheduleItem(
          occupancy.ResourceId,
          new Date(next),
          new Date(nextEnd),
          occupancy.Designation,
          occupancy.Description,
          defaultText,
          displayColor,
          occupancy.Type);
        array.push(intermediatItem);

        // increment day
        next = new Date(next);
        next.setDate(next.getDate() + 1);
      }

    }

    return array;
  }

  ngOnInit() {

    if (this.type === undefined) {
      throw new Error(`prop '${nameof<RoomScheduleComponent>('type')}' must be defined on RoomScheduleComponent`);
    }

    this.setDisplayWeek(this.currentDate);
    this.loadData();
  }

  /**
   * Navigate calendar to week of given date
   *
   * @param {Date} date
   * @memberof RoomScheduleComponent
   */
  setDisplayWeek(date: Date) {
    // monday
    this.firstDate = new Date(date);
    if (this.firstDate.getDay() !== 1) {
      this.firstDate.setDate(this.firstDate.getDate() - (this.firstDate.getDay() - 1)); // set to monday
    }
    // sunday
    this.lastDate = new Date(this.firstDate);
    this.lastDate.setDate(this.lastDate.getDate() + 7);
  }

  /**
   * Initialize and return a new TableModel instance
   *
   * @returns {TableModel}
   * @memberof RoomScheduleComponent
   */
  initNewTableModel(): TableModel {
    const model = new TableModel();

    // header
    for (let day = 0; day < 7; day++) {
      const d = new Date(this.firstDate.getFullYear(), this.firstDate.getMonth(), this.firstDate.getDate() + day);
      model.headers.push({ date: d, text: this.lang.formatWeekday(d) });
    }

    // data
    for (let hour = this.fromHour; hour <= this.toHour; hour++) { // every hour of a day (y-axis)
      const wh = new WeekHour(hour);

      for (let day = 0; day < 7; day++) { // every day of the week (x-axis)
        wh.hours[day] = new Hour(hour);
      }

      model.weekHours.push(wh);
    }

    return model;
  }

  /**
   * Navigate to next week
   *
   * @memberof RoomScheduleComponent
   */
  nextWeek() {
    const newDate = new Date(this.firstDate);
    newDate.setDate(newDate.getDate() + 7);
    this.setDisplayWeek(newDate);
    this.loadData();
  }

  /**
   * Navigate to previous week
   *
   * @memberof RoomScheduleComponent
   */
  prevWeek() {
    const newDate = new Date(this.firstDate);
    newDate.setDate(newDate.getDate() - 7);
    this.setDisplayWeek(newDate);
    this.loadData();
  }

  formatTime(hour: number): string {
    return StringHelper.formatTime(hour);
  }

  /**
   * load data from server and assign data to view
   *
   * @private
   * @memberof RoomScheduleComponent
   */
  private loadData() {
    this.isLoading = true;

    const start = this.firstDate;
    start.setHours(0);
    start.setMinutes(0);
    start.setSeconds(0);

    const end = this.lastDate;
    end.setHours(0);
    end.setMinutes(0);
    end.setSeconds(0);

    let observable: Observable<any>;
    switch (this.type) {
      case ResourceType.Raum:
        observable = this.room.getOccupanciesById(this.roomId, start, end);
        break;
      case ResourceType.Gerät:
        observable = this.device.getOccupanciesById(this.roomId, start, end);
        break;
      default:
        throw new Error(`Type '${ResourceTypeHelper.getString(this.type)}' not Implemented`);
    }

    observable.subscribe(data => {
      const occupancies = (data as Occupancy[]).map((o: Occupancy) => Occupancy.create(o));
        const dailyScheduleItems: {[date: string]: IMobileTableModel; } = { };

        // init items
        let indexDate = start;
        while (indexDate < end) {
          dailyScheduleItems[this.getKey(indexDate)] = { date: indexDate, scheduleItems: [] };
        indexDate = new Date(indexDate.getTime() + 24 * 3600 * 1000);
      }

      // map data
      occupancies.forEach(x => {
          RoomScheduleComponent.createScheduleItems(x, this.translate.get('OCCUPIED'), start, end).forEach((i: ScheduleItem) => {

            if (!DateHelper.Intersects(this.firstDate, end, i.from, i.to)) {
              return; // skip out of table-range items
            }

            // add to dict
            const key = this.getKey(i.from);
            if (dailyScheduleItems[key] === undefined) {
              dailyScheduleItems[key] = { date: i.from, scheduleItems: [] };
            }
            dailyScheduleItems[key].scheduleItems.push(i);
          });

      });

        this.mobileTableData = [];
        for (const key in dailyScheduleItems) {
        if (dailyScheduleItems.hasOwnProperty(key)) {
          // hour 22 ends at 22:59:59.999
          const endTime = new Date(this.getDateFromKey(key, this.toHour).getTime() + (3600 * 1000) - 1);
          const emptyItems = ScheduleItem.createEmptyScheduleItems(
            this.roomId,
            dailyScheduleItems[key].scheduleItems,
            this.getDateFromKey(key, this.fromHour),
            endTime, // hour 22 ends at 22:59:59.999
            this.ressourceInfo.isBookable
              ? (this.ressourceInfo.hasWarnings ? this.translate.get('CLICK_TO_REQUEST') : this.translate.get('CLICK_TO_RESERVE'))
              : this.translate.get('NOT_BOOKABLE'),
            this.ressourceInfo.isBookable
          );

          dailyScheduleItems[key].scheduleItems = dailyScheduleItems[key].scheduleItems.concat(emptyItems);
          this.mobileTableData.push(dailyScheduleItems[key]);
        }
      }

        const newModel = this.initNewTableModel();
        this.adjustDisplayOfSundays(newModel);

        this.items = [];
      this.mobileTableData.forEach(mtm => {
          mtm.scheduleItems.forEach(i => {
            // skip items that end before start of calendar
            if (i.to.getHours() < this.fromHour) {
              return;
            }

            let hourIndex = i.from.getHours() - this.fromHour;

            // ensure range
            if (hourIndex < 0) {
              hourIndex = 0; // put out of time-range items into first cell
            }
            if (hourIndex > newModel.weekHours.length - 1) {
              hourIndex = newModel.weekHours.length - 1;
            }

            if (i.to.getHours() >= this.toHour + 1) {
              i.to.setHours(this.toHour + 1);
              i.to.setMinutes(0);
              i.to.setSeconds(0);
              i.to.setMilliseconds(0);
            }

            const dayIndex = DateHelper.getWeekdayDifference(i.from, start);
            if (newModel.weekHours[hourIndex].hours[dayIndex] !== undefined) {
              newModel.weekHours[hourIndex].hours[dayIndex].scheduleItems.push(i);
            }
          });

        this.items.push({
          resourceId: this.roomId,
        isBookable: true,
        scheduleItems: mtm.scheduleItems,
        additionalColumsData: [],
        designation: this.eventoDayShortPipe.transform(mtm.date)
        + ' ' + this.eventoDatePipe.transform(mtm.date),
        warning: '',

        actionLabel: null,
        isBookingAllowed: null,
        isRessourceAvailable: null
        });

      });

        this.tableModel = newModel;

        this.isLoading = false;
    });

  }

        /**
        * Remove sunday if no workday
        *
        * @private
        * @memberof RoomScheduleComponent
        */
        private adjustDisplayOfSundays(model: TableModel) {
    for (let i = 0; i < this.mobileTableData.length; i++) {
      const element = this.mobileTableData[i];
        if (
        element.date.getDay() === 0
        && element.scheduleItems.length === 1
        && element.scheduleItems.filter(si => si.type === OccupancyType.Blocker).length === 1
        ) {
          // delete sunday items
          this.mobileTableData.splice(i, 1);
        // delete sunday table column
        model.weekHours.forEach((wh: WeekHour) => {
          wh.hours.splice(-1, 1);
        });
        // delete sunday header
        model.headers.splice(-1, 1);
        break;
        }

    }
  }

        showBgLight(index: number) {
    return index % 2 === 0;
  }

        /**
           * Handle click on "create reservation"
           *
           * @memberof CollapsibleScheduleComponent
           */
        onCreateReservation(params: ICreateReservationParams) {
          this.createReservation.emit(params);
  }

        private getKey(date: Date): string {
    return String(date.getFullYear())
        + StringHelper.padLeft(String(date.getMonth() + 1), 2, '0')
        + StringHelper.padLeft(String(date.getDate()), 2, '0');
  }

        private getDateFromKey(key: string, hour: number): Date {
    return new Date(
        Number(key.substring(0, 4)),
        Number(key.substring(4, 6)) - 1,
        Number(key.substring(6)),
        hour
        );
        }

}

        /**
         * Hour of the week (x-axis)
         *
         * @class WeekHour
         */
        class WeekHour {
          hour: number;
        hours: Hour[];

        constructor(hour: number) {
          this.hour = hour;
        this.hours = new Array<Hour>();
  }

}

          /**
           * Hour of the week (x-axis) containig days
           *
           * @class Hour
           */
          class Hour {

            hour: number;

          scheduleItems: ScheduleItem[];

          constructor(hour: number) {
            this.hour = hour;
          this.scheduleItems = [];
  }

}

          /**
           * Model of the table header
           *
           * @interface ITableHeader
           */
          interface ITableHeader {
            text: string;
          date: Date;
}

          class TableModel {

            /**
             * Table header data
             * the array index represents the day of the week
             *
             * @type {ITableHeader[]}
             * @memberof TableModel
             */
            headers: ITableHeader[] = [];

  /**
   * Hours of the table
   * the array index represents the hour of the day
   *
   * @type {Occupancy[]}
          * @memberof TableModel
          */
          weekHours: WeekHour[] = [];

}

          interface IMobileTableModel {
            date: Date;
          scheduleItems: ScheduleItem[];
}
